/**
 * @module public/js/utils/personalizedMediaUtils
 * @summary Browser-safe helpers for recording, previewing, and exporting personalized video.
 */

export const PERSONALIZED_MEDIA_MAX_DURATION_MS = 60 * 1000;
export const PERSONALIZED_MEDIA_MAX_VIDEO_BYTES = 100 * 1024 * 1024;
export const PERSONALIZED_MEDIA_MAX_THUMBNAIL_BYTES = 2 * 1024 * 1024;
export const PERSONALIZED_MEDIA_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export const PERSONALIZED_MEDIA_MIME_PREFERENCES = Object.freeze([
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
]);

/**
 * Select the first recording MIME type supported by the current browser.
 *
 * @param {{mediaRecorder?: typeof MediaRecorder, requireMp4?: boolean, preferences?: string[]}} [options]
 * @returns {string|null} Supported MIME type, or null when recording is unavailable.
 */
export function selectPersonalizedMediaMimeType({
  mediaRecorder = globalThis.MediaRecorder,
  requireMp4 = false,
  preferences = PERSONALIZED_MEDIA_MIME_PREFERENCES,
} = {}) {
  if (!mediaRecorder) return null;
  const candidates = Array.from(preferences || []).filter(
    (value) => !requireMp4 || String(value).startsWith('video/mp4')
  );
  if (typeof mediaRecorder.isTypeSupported !== 'function') {
    return requireMp4 ? null : '';
  }
  return (
    candidates.find((candidate) => mediaRecorder.isTypeSupported(candidate)) ||
    null
  );
}

/**
 * Return the stable file extension associated with a recorded video MIME type.
 *
 * @param {string} contentType Recording MIME type.
 * @returns {'mp4'|'webm'} File extension.
 */
export function personalizedMediaExtension(contentType) {
  return String(contentType || '')
    .toLowerCase()
    .includes('mp4')
    ? 'mp4'
    : 'webm';
}

/**
 * Sanitize a suggested personalized-video filename without including prospect data.
 *
 * @param {string} value Suggested basename.
 * @param {string} contentType Recording MIME type.
 * @returns {string} Download-safe filename with extension.
 */
export function buildPersonalizedMediaFileName(
  value = 'topsail-personalized-video',
  contentType = 'video/mp4'
) {
  const extension = personalizedMediaExtension(contentType);
  const basename = String(value || 'topsail-personalized-video')
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `${basename || 'topsail-personalized-video'}.${extension}`;
}

/**
 * Build a unique, privacy-safe export basename using a UTC recording timestamp.
 *
 * @param {{channel?:string,recordedAt?:Date}} [options] Filename context.
 * @returns {string} Filename basename without an extension or prospect data.
 */
export function buildTimestampedPersonalizedMediaBaseName({
  channel = 'video',
  recordedAt = new Date(),
} = {}) {
  const safeChannel =
    String(channel || 'video')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'video';
  const timestamp = new Date(recordedAt)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')
    .replace('T', '-');
  return `topsail-${safeChannel}-${timestamp}`;
}

/**
 * Build private Storage object paths for one seller-owned media asset.
 *
 * @param {{tenantId:string,uid:string,assetId:string,contentType:string}} params Path inputs.
 * @returns {{sourcePath:string,thumbnailPath:string,sourceFileName:string}}
 */
export function buildPersonalizedMediaStoragePaths({
  tenantId,
  uid,
  assetId,
  contentType,
}) {
  const safeTenantId = sanitizePersonalizedMediaPathSegment(
    tenantId,
    'unknown-tenant'
  );
  const safeUid = sanitizePersonalizedMediaPathSegment(uid, 'unknown-user');
  const safeAssetId = sanitizePersonalizedMediaPathSegment(
    assetId,
    'unknown-asset'
  );
  const extension = personalizedMediaExtension(contentType);
  const prefix = `tenants/${safeTenantId}/personalized-media/${safeUid}/${safeAssetId}`;
  return {
    sourcePath: `${prefix}/source.${extension}`,
    thumbnailPath: `${prefix}/thumbnail.jpg`,
    sourceFileName: `source.${extension}`,
  };
}

/**
 * Normalize one identifier for use as a private Cloud Storage path segment.
 *
 * @param {string} value Raw path segment.
 * @param {string} fallback Fallback when the sanitized value is empty.
 * @returns {string} Storage-safe segment.
 */
export function sanitizePersonalizedMediaPathSegment(value, fallback) {
  return (
    String(value || fallback)
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 120) || fallback
  );
}

/**
 * Convert a completed recording Blob into a named File for upload and local export.
 *
 * @param {Blob} blob Recorded bytes.
 * @param {{fileName?:string,contentType?:string,lastModified?:number}} [options]
 * @returns {File} Named browser File.
 */
export function createPersonalizedMediaFile(
  blob,
  {
    fileName = 'topsail-personalized-video',
    contentType = blob?.type || 'video/webm',
    lastModified = Date.now(),
  } = {}
) {
  if (!(blob instanceof Blob) || blob.size <= 0) {
    throw new Error('The recording is empty. Record the video again.');
  }
  return new File(
    [blob],
    buildPersonalizedMediaFileName(fileName, contentType),
    {
      type: contentType,
      lastModified,
    }
  );
}

/**
 * Capture a JPEG thumbnail from a recorded video Blob using an off-screen video.
 *
 * @param {Blob} videoBlob Recorded video bytes.
 * @param {{documentRef?:Document,urlApi?:typeof URL,maxWidth?:number,quality?:number,seekSeconds?:number}} [options]
 * @returns {Promise<{blob:Blob,width:number,height:number}>} Thumbnail bytes and dimensions.
 */
export async function capturePersonalizedMediaThumbnail(
  videoBlob,
  {
    documentRef = globalThis.document,
    urlApi = globalThis.URL,
    maxWidth = 640,
    quality = 0.82,
    seekSeconds = 0.2,
  } = {}
) {
  if (!(videoBlob instanceof Blob) || videoBlob.size <= 0) {
    throw new Error('A recorded video is required to create a thumbnail.');
  }
  if (!documentRef?.createElement || !urlApi?.createObjectURL) {
    throw new Error('Video thumbnails are unavailable in this browser.');
  }

  const video = documentRef.createElement('video');
  const objectUrl = urlApi.createObjectURL(videoBlob);
  video.muted = true;
  video.preload = 'metadata';
  video.playsInline = true;
  video.src = objectUrl;

  try {
    await waitForMediaEvent(video, 'loadedmetadata');
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime = Math.min(
      Math.max(0, seekSeconds),
      Math.max(0, duration)
    );
    if (targetTime > 0) {
      video.currentTime = targetTime;
      await waitForMediaEvent(video, 'seeked');
    }

    const sourceWidth = Math.max(1, Number(video.videoWidth || 1));
    const sourceHeight = Math.max(1, Number(video.videoHeight || 1));
    const scale = Math.min(1, maxWidth / sourceWidth);
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = documentRef.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Thumbnail canvas is unavailable.');
    context.drawImage(video, 0, 0, width, height);
    const blob = await canvasToJpegBlob(canvas, quality);
    return { blob, width, height };
  } finally {
    video.removeAttribute?.('src');
    urlApi.revokeObjectURL?.(objectUrl);
  }
}

/**
 * Download a local media File using the browser's established object-URL flow.
 *
 * @param {File|Blob} file Media bytes.
 * @param {{documentRef?:Document,urlApi?:typeof URL,fileName?:string}} [options]
 * @returns {string} Filename submitted to the browser download manager.
 */
export function downloadPersonalizedMediaFile(
  file,
  {
    documentRef = globalThis.document,
    urlApi = globalThis.URL,
    fileName = file?.name || 'topsail-personalized-video',
  } = {}
) {
  if (!(file instanceof Blob) || file.size <= 0) {
    throw new Error('A recorded video is required for local export.');
  }
  if (!documentRef?.createElement || !urlApi?.createObjectURL) {
    throw new Error('Local video export is unavailable in this browser.');
  }
  const contentType = file.type || 'video/webm';
  const safeFileName = buildPersonalizedMediaFileName(fileName, contentType);
  const objectUrl = urlApi.createObjectURL(file);
  const anchor = documentRef.createElement('a');
  anchor.href = objectUrl;
  anchor.download = safeFileName;
  anchor.hidden = true;
  documentRef.body?.appendChild(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout?.(() => urlApi.revokeObjectURL?.(objectUrl), 0);
  return safeFileName;
}

/**
 * Save media to a reusable local file when the browser supports file handles.
 *
 * The first overwrite-enabled save asks the seller to choose the destination.
 * Later saves with the same safe filename reuse that handle and replace the
 * file contents instead of creating browser-managed numbered downloads.
 *
 * @param {File|Blob} file Media bytes.
 * @param {{documentRef?:Document,urlApi?:typeof URL,fileName?:string,overwriteExisting?:boolean,saveFilePicker?:Function|null,fileHandle?:object|null}} [options]
 * @returns {Promise<{fileName:string,fileHandle:object|null}>} Local save result.
 */
export async function savePersonalizedMediaFile(
  file,
  {
    documentRef = globalThis.document,
    urlApi = globalThis.URL,
    fileName = file?.name || 'topsail-personalized-video',
    overwriteExisting = false,
    saveFilePicker = typeof globalThis.showSaveFilePicker === 'function'
      ? globalThis.showSaveFilePicker.bind(globalThis)
      : null,
    fileHandle = null,
  } = {}
) {
  if (!(file instanceof Blob) || file.size <= 0) {
    throw new Error('A recorded video is required for local export.');
  }

  const contentType = file.type || 'video/webm';
  const safeFileName = buildPersonalizedMediaFileName(fileName, contentType);
  if (
    !overwriteExisting ||
    (!fileHandle && typeof saveFilePicker !== 'function')
  ) {
    return {
      fileName: downloadPersonalizedMediaFile(file, {
        documentRef,
        urlApi,
        fileName: safeFileName,
      }),
      fileHandle: null,
    };
  }

  let reusableFileHandle = fileHandle;
  if (!reusableFileHandle) {
    const extension = `.${personalizedMediaExtension(contentType)}`;
    const acceptedContentType = contentType.split(';')[0];
    reusableFileHandle = await saveFilePicker({
      suggestedName: safeFileName,
      startIn: 'downloads',
      types: [
        {
          description: 'Personalized video',
          accept: { [acceptedContentType]: [extension] },
        },
      ],
    });
  }

  const writable = await reusableFileHandle.createWritable();
  await writable.write(file);
  await writable.close();

  return {
    fileName: reusableFileHandle.name || safeFileName,
    fileHandle: reusableFileHandle,
  };
}

/**
 * Await one successful media event or reject on a media error.
 *
 * @param {HTMLMediaElement} element Media element.
 * @param {string} eventName Successful event name.
 * @returns {Promise<void>}
 */
function waitForMediaEvent(element, eventName) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      element.removeEventListener(eventName, onReady);
      element.removeEventListener('error', onError);
    };
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Unable to read the recorded video.'));
    };
    element.addEventListener(eventName, onReady, { once: true });
    element.addEventListener('error', onError, { once: true });
  });
}

/**
 * Convert one canvas frame to a JPEG Blob.
 *
 * @param {HTMLCanvasElement} canvas Source canvas.
 * @param {number} quality JPEG quality from zero to one.
 * @returns {Promise<Blob>}
 */
function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Unable to create the video thumbnail.'));
          return;
        }
        if (blob.size > PERSONALIZED_MEDIA_MAX_THUMBNAIL_BYTES) {
          reject(new Error('The generated video thumbnail is too large.'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}
